import { ref } from 'vue';
import { useAuthStore } from 'stores/auth';
import { useServicesStore } from 'stores/services';
import { USER_PERMISSIONS } from 'src/constants/Permissions';
import { globalRouter } from 'boot/router-global';
import { useAdminStore } from 'stores/admin';
import { Notify } from 'quasar';

export function usePopupHandler() {
  const popup = ref<Window | null>(null);

  async function openPopup(url: string, isLogout = false) {
    const width = 1000;
    const height = 1000;
    const left = window.screenX + (window.outerWidth - width) / 2;
    const top = window.screenY + (window.outerHeight - height) / 2;
    const features = `width=${width},height=${height},left=${left},top=${top},scrollbars=yes,resizable=yes`;

    popup.value = window.open(url, '_blank', features);

    if (
      !popup.value ||
      popup.value.closed ||
      typeof popup.value.closed === 'undefined'
    ) {
      console.warn('Popup was blocked by the browser.');

      Notify.create({
        type: 'negative',
        message:
          'Popup was blocked. Please allow popups for this site and try again.',
        position: 'top',
      });

      return;
    }

    console.log('Popup opened');

    try {
      await waitForPopupClose(popup.value, url);

      await onPopupClosed(isLogout);
    } catch (error) {
      console.error('Popup monitoring error:', error);
    }
  }

  function waitForPopupClose(
    windowProxy: WindowProxy,
    url: string
  ): Promise<string> {
    return new Promise((resolve, reject) => {
      const check = () => {
        if (!windowProxy) {
          reject(new Error('Window proxy lost reference'));
          return;
        }

        if (windowProxy.closed) {
          resolve(`Popup for ${url} has closed!`);
        } else {
          setTimeout(check, 500);
        }
      };
      check();
    });
  }

  async function onPopupClosed(isLogout: boolean) {
    console.log('Popup closed callback running...');

    const $route = globalRouter.currentRoute.value;
    const $store = useAuthStore();

    if (isLogout) {
      $store.oidcLogout();
      return;
    }

    const $adminStore = useAdminStore();
    const $servicesStore = useServicesStore();

    try {
      await $store.checkLoggedIn();
      await $store.getUserDetails(true);

      if ($route.query?.redirect) {
        void globalRouter
          .replace($route.query.redirect.toString())
          .catch(() => {
            void globalRouter.replace('/');
          });
      }

      if (
        $store.user.permissions.includes(
          USER_PERMISSIONS.CLINIC_VIEW_RELEASE_NOTES
        )
      ) {
        await $servicesStore.getReleaseNotes();
        $servicesStore.showLatestReleaseNoteModal();
      }

      if ($store.isClinician && $store.user.staff && $store.user.staff.id) {
        $adminStore.getExpiringCompetencies($store.user.staff.id);
      }
    } catch (e) {
      console.error('Failed to refresh session after popup close:', e);
    }
  }

  return { openPopup };
}
